import { useEffect, useRef, useState } from 'preact/hooks';
import { FieldHeader } from '../components/FieldHeader';
import { route } from 'preact-router';
import { BASE_PATH } from '../../shared/types';
import { FEEDBACK_MAX_LENGTH } from '../../shared/feedback';
import { useMe } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';
import { AccountMenu } from '../components/AccountMenu';
import { HelpLink } from '../components/HelpLink';
import { AppIcon } from '../components/AppIcon';

export function Feedback() {
  const { me } = useMe();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Prevent two submit events before the disabled button has rendered.
  const sending = useRef(false);
  useEffect(() => { if (me === null) route(`${BASE_PATH}/login`, true); }, [me]);
  useEffect(() => { if (sent) heading.current?.focus(); }, [sent]);
  const submit = async (event: Event) => {
    event.preventDefault();
    if (sending.current) return;
    if (!message.trim() || message.length > FEEDBACK_MAX_LENGTH) {
      setInvalid(true); setError(message.trim() ? 'Please keep your feedback to 5,000 characters or fewer.' : 'Please write a message before sending.');
      input.current?.focus(); return;
    }
    sending.current = true; setBusy(true); setError(''); setInvalid(false);
    try {
      await api.post('/feedback', { message: message.trim() });
      setMessage(''); setSent(true);
    } catch (error) {
      const code = error instanceof ApiError ? error.code : '';
      const fieldError = code === 'invalid_feedback' || code === 'too_long' || code === 'too_large';
      setInvalid(fieldError);
      setError(code === 'rate_limited' ? 'You’ve sent several messages recently. Please try again in an hour.'
        : code === 'unauthorized' ? 'Your session has ended. Please sign in again before sending your feedback.'
        : fieldError ? 'Please write a message of 5,000 characters or fewer.'
        : 'We couldn’t send your feedback. Your message is still here—please try again.');
      if (fieldError) input.current?.focus();
    } finally { sending.current = false; setBusy(false); }
  };
  if (!me) return <main class="container"><p class="muted" role="status">Loading…</p></main>;
  return <main class="container dashboard feedback-page">
    <div class="dashboard-brandbar"><Brand /><div class="brandbar-actions"><HelpLink /><AccountMenu email={me.email} isAdmin={me.isAdmin} /></div></div>
    <a class="back" href={`${BASE_PATH}/dashboard`}>← Your events</a>
    <section class="feedback-panel" aria-labelledby="feedback-title">
      <p class="eyebrow">Feedback</p>
      <h1 id="feedback-title" ref={heading} tabIndex={-1}>{sent ? 'Thanks for the feedback.' : 'Share your feedback.'}</h1>
      {sent ? <>
        <p role="status">Your message has been sent.</p>
        <p class="muted">We can reply to you at <strong>{me.email}</strong>.</p>
        <div class="row wrap feedback-actions"><a class="button" href={`${BASE_PATH}/dashboard`}>Back to your events</a>
          <button class="secondary" type="button" onClick={() => { setSent(false); requestAnimationFrame(() => input.current?.focus()); }}>Send another message</button></div>
      </> : <>
        <p class="muted">Tell us what’s working, what could be better, or where you got stuck.</p>
        <form onSubmit={submit} class="stack feedback-form" noValidate>
          <FieldHeader htmlFor="feedback-message" label="Your feedback" error={invalid ? error : null} errorId="feedback-field-error" />
          <textarea id="feedback-message" ref={input} rows={7} maxLength={FEEDBACK_MAX_LENGTH} required value={message} disabled={busy}
            aria-invalid={invalid ? 'true' : undefined} aria-describedby={`${invalid && error ? 'feedback-field-error ' : ''}feedback-guidance feedback-length${error && !invalid ? ' feedback-error' : ''}`}
            onInput={(event) => { setMessage(event.currentTarget.value); setInvalid(false); setError(''); }} />
          <p id="feedback-length" class="muted small feedback-length">{message.length.toLocaleString()} / {FEEDBACK_MAX_LENGTH.toLocaleString()} characters</p>
          {error && !invalid && <p id="feedback-error" class="alert" role="alert">{error}</p>}
          <p id="feedback-guidance" class="muted small">Your message will be sent with your account email so we can reply.</p>
          <div><button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send feedback'} <AppIcon name="arrow" /></button></div>
        </form>
      </>}
    </section>
  </main>;
}
